import { Dialog, Form, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { TbDeviceFloppy } from "react-icons/tb";
import {
  MortgagePatchBodySchema,
  RFQ_STATUS_LABELS,
  RFQ_STATUSES,
} from "shared";
import type { z } from "zod";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { FormGroup } from "@/components/form/FormGroup";
import { QueryKeys } from "@/LinkGen";
import { useMortgage } from "@/mortgages/hooks";

const Schema = MortgagePatchBodySchema.pick({ status: true }).required();
type FormValues = z.infer<typeof Schema>;

interface ChangeRfqStatusDialogProps {
  mortgageID: number;
}

export const ChangeRfqStatusDialog = ({
  mortgageID,
}: ChangeRfqStatusDialogProps) => {
  const dialog = useDialog();
  const mortgage = useMortgage(mortgageID);
  const queryClient = useQueryClient();

  const { handleSubmit, formState, register } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      status: mortgage.status,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    await Api.MortgagePatch(mortgageID, { status: values.status });
    await queryClient.refetchQueries({
      queryKey: QueryKeys.Mortgage(mortgageID),
    });
    dialog.close();
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Change status</Dialog.Header>
      <Dialog.Body>
        <FormGroup label="Status" htmlFor="status">
          <Form.Select
            id="status"
            error={!!formState.errors.status}
            {...register("status")}
          >
            {RFQ_STATUSES.map((s) => (
              <option key={s} value={s}>
                {RFQ_STATUS_LABELS[s]}
              </option>
            ))}
          </Form.Select>
        </FormGroup>
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
